import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ExternalLinkService, HrefLinkDirective, stringOrEmpty } from '@big-direkt/utils/shared';

type Target = '_blank' | '_parent' | '_self' | '_top' | '';

@Component({
    selector: 'big-ui-link',
    templateUrl: './link.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'block',
    },
    imports: [HrefLinkDirective, RouterLink, NgTemplateOutlet],
})
export class LinkComponent {
    private readonly externalLinkService = inject(ExternalLinkService);
    private readonly router = inject(Router);
    public readonly linkClicked = output<'external_link' | 'internal_link'>();
    public readonly external = output<boolean>();

    public readonly href = input.required<string, string | undefined>({ transform: stringOrEmpty });
    public readonly linkClass = input<string, string | undefined>(undefined, {
        transform: value => this.appendLinkClassDefaults(stringOrEmpty(value)),
    });
    public readonly linkId = input<string | undefined>();
    public readonly download = input<string | undefined>();
    public readonly target = input<Target | undefined>();
    public readonly srOnly = input<boolean>(false);

    public readonly computedTarget = computed(() => this.target() ?? this.externalLinkService.getTarget(this.isExternal()));
    public readonly hrefUrlTree = computed(() => this.router.parseUrl(this.href()));
    public readonly isExternal = computed(() => this.externalLinkService.isExternal(this.href()));

    public constructor() {
        effect(() => {
            this.external.emit(this.isExternal());
        });
    }

    public onLinkClicked(): void {
        this.linkClicked.emit(this.isExternal() ? 'external_link' : 'internal_link');
    }

    private appendLinkClassDefaults(value: string): string {
        let classList = value;

        if (!classList.includes('primary') && !classList.includes('secondary') && !classList.includes('none')) {
            classList = `${classList} tertiary`;
        }

        if (classList.includes('tertiary')) {
            classList = `${classList} relative`;
        }

        return classList;
    }
}
